/**
 * The HUD's looks as engine overlay paint, in CSS px. A funded selection draws by its analysis's style (Mighty Map's
 * style spec: one look, or a look per value). Targets and the towers demo keep production's sizes and colours:
 * a reached target is a 4 px dot in its level's colour, an unreached one a small white dot with a dark rim; a
 * constructed tower is a larger green dot with a white rim, a scheduled one a small grey dot. Points draw as
 * circles.
 */
import type { OverlayPaint } from '@mighty-map/map-engine';
import type { Bands, PointPaint, PointStyle } from './analyses';
import { towerDone, TOWER_TODO } from './towers';

export function toOverlay(p: PointPaint): OverlayPaint {
  return {
    fill: p.fill,
    fillOpacity: p.fillOpacity ?? 1,
    radius: p.radius,
    markerStroke: p.stroke ?? p.fill,
    markerStrokeWidth: p.stroke ? (p.strokeWidth ?? 1) : 0,
  };
}

const REACHED_RADIUS = 4;
const UNREACHED: Omit<PointPaint, 'fill'> = { radius: 2.67, stroke: 'rgba(20,26,34,.6)', strokeWidth: 0.93 };

/** A target's look at its level; null when the analysis hides unreached targets. */
export function targetPaint(bands: Bands, level: number): OverlayPaint | null {
  const L = bands.levels.find((l) => l.v === level);
  if (L) return toOverlay({ fill: L.color, radius: REACHED_RADIUS });
  if (bands.hideNone) return null;
  return toOverlay({ fill: bands.none, ...UNREACHED });
}

/** A funded candidate's look, by the analysis's selection style. */
export function selectionPaint(style: PointStyle, attrs: Readonly<Record<string, unknown>>): OverlayPaint {
  if (style.kind === 'simple') return toOverlay(style.paint);
  const raw = attrs[style.field];
  const v = typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean' ? String(raw) : '';
  return toOverlay(style.classes.find((c) => c.value === v)?.paint ?? style.other);
}

export const towerPaint = (done: boolean): OverlayPaint =>
  toOverlay(
    done
      ? { fill: towerDone(), radius: 4.67, stroke: '#ffffff', strokeWidth: 1.33 }
      : { fill: TOWER_TODO, radius: 2.67, stroke: 'rgba(255,255,255,.35)', strokeWidth: 0.67 },
  );
